import { Suspense, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigate } from '@remix-run/react';
import {
  ConfirmActionModalElement,
  ConnectedAccountsModalElement,
  SocialLoginProviderType,
} from '@shared/dream-components';
import { useQueryClient } from '@tanstack/react-query';

import useDeleteIdentity from '~/hooks/useDeleteIdentity';
import { identitiesKeys } from '~/hooks/useLoaders/useIdentities';
import type { Identity } from '~/interfaces/identity';
import type { Integration } from '~/interfaces/integration';

import AccountConnection from './AccountConnection';
import AccountConnectionSkeleton from './AccountConnectionSkeleton';

interface Props {
  isOpen: boolean;
  subscriberId: string;
  identities: Identity[];
  socialProviders: Integration[];
  closeModal: () => void;
}

const ConnectedAccountsModal = ({ isOpen, subscriberId, identities, socialProviders, closeModal }: Props) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation('common', { keyPrefix: 'connected_accounts_modal' });
  const [pendingSpotifyDisconnect, setPendingSpotifyDisconnect] = useState<Identity | null>(null);

  const { deleteIdentity, deletingIdentityId } = useDeleteIdentity({
    onSuccess: () => {
      setPendingSpotifyDisconnect(null);
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: identitiesKeys.bySubscriber(subscriberId) });
      }, 100);
    },
  });

  const connectedAccountsPair = useMemo(
    () =>
      socialProviders.map((provider) => {
        const connectedAccount = identities.find((identity) => identity.provider === provider.provider);
        return { provider, connectedAccount };
      }),
    [identities, socialProviders],
  );

  const handleConnect = (provider: Integration) => {
    const providerName = provider.provider.toLowerCase();
    navigate(`/oauth/${providerName}/authorize?subscriber_id=${subscriberId}`, { replace: true });
  };

  const handleDisconnect = (identity: Identity) => {
    if (identity.provider === SocialLoginProviderType.SPOTIFY) {
      setPendingSpotifyDisconnect(identity);
      return;
    }

    deleteIdentity(identity.id, identity.provider);
  };

  const handleConfirmSpotifyDisconnect = () => {
    if (!pendingSpotifyDisconnect) return;

    deleteIdentity(pendingSpotifyDisconnect.id, pendingSpotifyDisconnect.provider);
  };

  if (!isOpen || !socialProviders.length) return null;

  return (
    <>
      <ConnectedAccountsModalElement
        element={{
          type: 'connectedAccountsModal',
        }}
        isOpen={isOpen}
        onOpenChange={() => closeModal()}
        title={t('title')}
        message={t('message')}
        closeLabel={t('close')}
      >
        <div className="flex flex-col gap-2">
          {connectedAccountsPair.map(({ provider, connectedAccount }) => (
            <Suspense key={provider.id} fallback={<AccountConnectionSkeleton />}>
              {connectedAccount ? (
                <AccountConnection
                  socialLoginProvider={provider}
                  connectedIdentity={connectedAccount}
                  onDisconnect={handleDisconnect}
                  isDeleting={deletingIdentityId === connectedAccount.id}
                />
              ) : (
                <AccountConnection socialLoginProvider={provider} connectedIdentity={null} onConnect={handleConnect} />
              )}
            </Suspense>
          ))}
        </div>
      </ConnectedAccountsModalElement>

      <ConfirmActionModalElement
        element={{ type: 'confirmActionModal' }}
        isOpen={pendingSpotifyDisconnect !== null}
        isSubmitting={deletingIdentityId === pendingSpotifyDisconnect?.id}
        title={t('spotify_disconnect.title')}
        message={t('spotify_disconnect.message')}
        submitLabel={t('spotify_disconnect.submit')}
        cancelLabel={t('spotify_disconnect.cancel')}
        submitVariant="danger"
        onCancel={() => setPendingSpotifyDisconnect(null)}
        onSubmit={handleConfirmSpotifyDisconnect}
        onOpenChange={(open) => {
          if (!open) {
            setPendingSpotifyDisconnect(null);
          }
        }}
      >
        {null}
      </ConfirmActionModalElement>
    </>
  );
};

export default ConnectedAccountsModal;
